import { extractBlobErrorMessage } from '@/utils/documentDownloadErrors';
export { extractBlobErrorMessage } from '@/utils/documentDownloadErrors';
/**
 * One-click mock exam API — Step 1 (exam sheet OCR), Step 2 (generation) and
 * admin prompt settings.
 *
 * See SPEC-ONECLICK-OCR-001 and SPEC-ONECLICK-BILLING-001. The one-click
 * routes are open to every signed-in user and charge points (OCR 2 per page,
 * generation 4 per sub-question); the prompt-settings routes stay admin-only.
 * The Bearer token is auto-injected by the shared axios instance's request
 * interceptor.
 */

import api, { NO_RETRY, userPreferencesApi } from '@/services/api';
import type { CreateJobResponse } from '@/types/api';
import type { OneclickFormat } from '@/types/oneclickFormat';
import { isInsufficientCreditError } from '@/utils/insufficientCredit';

// OCR is an expensive, non-idempotent, long-running AI call — give it a generous
// timeout matching the backend's synchronous worker proxy budget (REQ-OCR-018/034).
const OCR_TIMEOUT_MS = 300000;

export type PromptSettingFeature = 'ocr' | 'oneclick_mock';

export interface OneClickOutputContract {
  template: string;
  count_exact: string;
  count_cap: string;
}

export interface PromptSetting {
  output_contract_defaults?: OneClickOutputContract;
  /** Server default for params.sample_originals_directive (oneclick_mock only). */
  sample_originals_directive_default?: string;
  feature: PromptSettingFeature;
  model: string;
  prompt: string;
  params: Record<string, unknown> | null;
  updated_at: string;
}

export interface UpdatePromptSettingRequest {
  model: string;
  prompt: string;
  params?: Record<string, unknown> | null;
}

export interface PromptPreset {
  id: number;
  feature: PromptSettingFeature;
  name: string;
  model: string;
  prompt: string;
  params: Record<string, unknown> | null;
  updated_at: string;
}

export interface SavePromptPresetRequest {
  name: string;
  model: string;
  prompt: string;
  params?: Record<string, unknown> | null;
}

export interface OcrResponse {
  success: true;
  ocr_text: string;
  model: string;
  ocr_metadata?: OcrResultMetadata | null;
}

export interface OcrResultMetadata {
  complete: boolean;
  warning: string | null;
  attempts: { model: string; complete: boolean; characters: number; reason?: string }[];
}

export interface OneClickOcrDraft {
  original_available?: boolean;
  id: number;
  title: string | null;
  category: string;
  filename: string | null;
  ranges: string | null;
  ocr_text: string | null;
  ocr_metadata?: OcrResultMetadata | null;
  /** Lesson originals linked to sample question numbers; `[]` when none (absent from older servers). */
  sample_originals?: SampleOriginal[];
  created_at: string;
  updated_at: string;
}

/**
 * One lesson original and the sample question numbers rewritten from it
 * (SPEC-ONECLICK-VARIATION-001 REQ-OCV-001). Numbers are "1"~"999" strings in numeric order.
 */
export interface SampleOriginal {
  text: string;
  question_numbers: string[];
}

export interface CreateDraftRequest {
  title: string;
  category: string;
  filename: string;
  ranges: string;
  ocr_text: string;
  ocr_job_id?: string;
}

export interface PendingOcrResult {
  job_id: string;
  title: string | null;
  filename: string | null;
  ranges: string | null;
  ocr_text: string;
  ocr_metadata: OcrResultMetadata | null;
}

export interface PendingOcrSummary {
  id: string;
  title: string | null;
  filename: string | null;
  characters: number;
}

/** `sample_originals`: omit to keep the stored links, `[]` or `null` to clear them. */
export type UpdateDraftRequest = Partial<CreateDraftRequest> & { sample_originals?: SampleOriginal[] | null };

// --- Step 2: question generation (SPEC-ONECLICK-GEN-001) ---

// @MX:NOTE: [AUTO] Same 1~40 range as the server (services/worker_request_safety.py
// MIN/MAX_ONECLICK_QUESTION_COUNT); the route rejects anything else with 400
// (SPEC-ONECLICK-BILLING-001 REQ-OCB-003).
export const ONECLICK_QUESTION_COUNT_MIN = 1;
export const ONECLICK_QUESTION_COUNT_MAX = 40;
/** Cap of an unspecified-count request (server ONECLICK_UNSPECIFIED_QUESTION_CAP, SPEC-ONECLICK-VARIATION-001 REQ-OCV-018). */
export const ONECLICK_UNSPECIFIED_QUESTION_CAP = 30;

/** Generation model choice: the admin default, or one of the two admin-configured alternatives. */
export type OneClickModelVariant = 'default' | 'different_1' | 'different_2';

export interface GenerateRequest {
  model_variant?: OneClickModelVariant;
  /** Legacy flag sent with a non-default model_variant: a server without model_variant still uses a different model. */
  generate_differently?: boolean;
  ocr_draft_id: number;
  /** Selected passage ids in submission order (REQ-GEN-008: selection order = generation order). */
  passage_ids: number[];
  /** Requested total sub-question count (1~40). Omit the key for "at most 30, charged per generated question". */
  question_count?: number | null;
  /**
   * "지문을 변형하여 출제" options keyed by passage id (as a string), only for
   * selected passages with the option on. Omit the key when there are none.
   */
  passage_transforms?: Record<string, PassageTransformRequest>;
  /**
   * Vary the passages after the sample's linked originals (SPEC-ONECLICK-VARIATION-001
   * REQ-OCV-010). Omit the key when not used; never sent together with passage_transforms.
   */
  use_sample_originals?: boolean;
}

/** One passage's transform levels (same ranges as the workbook transform options). */
export interface PassageTransformRequest {
  difficulty_level: 1 | 2 | 3;
  length_level: 1 | 2 | 3;
  content_change_mode: 0 | 1 | 2 | 3;
}

/** 202 body: the queued background generation job (same queue fields as /api/generation-jobs). */
export type GenerateResponse = CreateJobResponse;

export interface OneClickGenerationDraft {
  id: number;
  title: string | null;
  category: string;
  source_ocr_draft_id: number | null;
  passage_ids: number[] | null;
  result_text: string | null;
  /** Set when the draft was saved by a background generation job; null for manual saves. */
  job_id?: string | null;
  /** Model / reasoning effort used for a job-made draft; the API sends these only to the tester account. */
  generation_model?: string | null;
  generation_reasoning_effort?: string | null;
  /** SPEC-ONECLICK-EXPORT-001 fields; the last three are recomputed by the server on every response. */
  analysis_text?: string | null;
  requested_question_count?: number | null;
  generated_question_count?: number | null;
  export_ready?: boolean;
  export_block_reason?: string | null;
  created_at: string;
  updated_at: string;
}

export type GenerationDraftExportFormat = 'word' | 'hwpx' | 'hwp';

/** What the one-click download modal sends with the format (design.md §3.1). */
export interface OneclickExportRequest {
  /** The whole one-click format shown in the modal. */
  oneclickFormat: OneclickFormat;
  /** The open editor accepts the merged server value after all pending saves finish. */
  resolveFormat?: (saved?: OneclickFormat) => OneclickFormat;
  /** The saved exam date as typed. An empty string drops the date item. */
  examDate: string;
}

/** 200 body of POST /api/oneclick/ocr/preflight (SPEC-ONECLICK-BILLING-001 §5.4). */
export interface OcrPreflightResponse {
  page_count: number;
  points: number;
}

/**
 * JSON error body of a failed one-click request — the shared client rejects with
 * `{ message, status, details: <body> }`; a raw axios error keeps `response.data`.
 */
export function oneclickErrorBody(error: unknown): { error?: string; error_code?: string } | null {
  const candidate =
    (error as { details?: unknown } | null)?.details ??
    (error as { response?: { data?: unknown } } | null)?.response?.data;
  return candidate && typeof candidate === 'object' && !(candidate instanceof Blob)
    ? (candidate as { error?: string; error_code?: string })
    : null;
}

/** A failed one-click request was a 402 `insufficient_credits` (REQ-OCB-020 (h)). */
export function isOneClickCreditError(error: unknown): boolean {
  const body = oneclickErrorBody(error);
  return body != null && isInsufficientCreditError({ error: body.error ?? '', error_code: body.error_code ?? null });
}

export interface SendToWorkbookRequest {
  workbook_id: number;
  /** null creates the next `실전모의고사 N회` sheet. */
  sheet_id: number | null;
}

export interface SendToWorkbookResponse {
  workbook_id: number;
  sheet_id: number;
  sheet_name: string;
  created_sheet: boolean;
  appended_rows: number;
  row_count: number;
}

/** `filename*` (RFC 5987) first, then the ASCII `filename`, then the fallback. */
export function parseContentDispositionFilename(header: string | undefined | null, fallback: string): string {
  if (!header) return fallback;
  const utf8 = header.match(/filename\*=UTF-8''([^;\s]+)/i);
  if (utf8) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      // fall through to the ASCII name
    }
  }
  const ascii = header.match(/filename="([^"]*)"/i) ?? header.match(/filename=([^;\s]+)/i);
  return ascii && ascii[1] ? ascii[1] : fallback;
}



export interface CreateGenerationDraftRequest {
  title: string;
  category: string;
  source_ocr_draft_id: number | null;
  passage_ids: number[];
  result_text: string;
}

export type UpdateGenerationDraftRequest = Partial<CreateGenerationDraftRequest>;

export const oneclickApi = {
  /**
   * Run OCR on uploaded exam sheets, extracting the given question numbers.
   * `files` order is page order — the server labels each file "=== N페이지 ==="
   * for the AI (REQ-OCR-037/039). `ranges` is the user's free-text selection
   * (e.g. "7-끝", "서답형 전체") — it substitutes the
   * `{범위}` prompt token server-side.
   */
  async runOcr(files: File[], ranges: string): Promise<OcrResponse> {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    formData.append('ranges', ranges);

    // skipRetry: OCR is a costly, non-idempotent AI call (REQ-OCR-022) — an
    // automatic retry after a slow/failed response leg would re-run the whole
    // OCR pass and could double-charge the AI call.
    const response = await api.post<OcrResponse>('/api/oneclick/ocr', formData, {
      ...NO_RETRY,
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: OCR_TIMEOUT_MS,
    });
    return response.data;
  },

  async startOcr(files: File[], ranges: string, onUploadProgress?: (percentage: number | null) => void): Promise<CreateJobResponse> {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    formData.append('ranges', ranges);
    const response = await api.post<CreateJobResponse>('/api/oneclick/ocr/jobs', formData, {
      ...NO_RETRY, headers: { 'Content-Type': 'multipart/form-data' }, timeout: OCR_TIMEOUT_MS,
      onUploadProgress: (event) => onUploadProgress?.(
        event.total && event.loaded >= event.total ? 100
          : event.total ? Math.min(99, Math.floor(event.loaded / event.total * 100)) : null,
      ),
    });
    return response.data;
  },

  /**
   * Exact PDF page count and points before an OCR job is queued (REQ-OCB-015).
   * Nothing is charged or stored. skipRetry: the upload can be up to 20MB.
   */
  async preflightOcr(file: File): Promise<OcrPreflightResponse> {
    const formData = new FormData();
    formData.append('files', file);
    const response = await api.post<OcrPreflightResponse>('/api/oneclick/ocr/preflight', formData, {
      ...NO_RETRY, headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /**
   * Current balance for the one-click points dialogs, or null when it cannot be
   * read; a failure is never shown as 0
   * (REQ-OCB-020 (e)); the server's 402 stays the final check.
   */
  async getCredits(): Promise<number | null> {
    try {
      const response = await api.get<{ credits?: unknown }>('/api/user/credits');
      const credits = response.data?.credits;
      return typeof credits === 'number' && Number.isFinite(credits) ? credits : null;
    } catch {
      return null;
    }
  },

  async retryOcr(jobId: string): Promise<CreateJobResponse> {
    const response = await api.post<CreateJobResponse>(`/api/oneclick/ocr/jobs/${jobId}/retry`, {}, NO_RETRY);
    return response.data;
  },

  async getDrafts(): Promise<OneClickOcrDraft[]> {
    const response = await api.get<{ drafts: OneClickOcrDraft[] }>('/api/oneclick/drafts');
    return response.data.drafts;
  },

  async getPendingOcrResult(jobId: string): Promise<PendingOcrResult> {
    const response = await api.get<PendingOcrResult>(`/api/oneclick/ocr/jobs/${encodeURIComponent(jobId)}/pending-result`);
    return response.data;
  },

  async getPendingOcrResults(): Promise<PendingOcrSummary[]> {
    const response = await api.get<{ pending_results: PendingOcrSummary[] }>('/api/oneclick/ocr/pending-results');
    if (!Array.isArray(response.data?.pending_results)) {
      throw new Error('편집 대기 중인 시험지 읽기 목록 응답이 올바르지 않습니다.');
    }
    return response.data.pending_results;
  },

  async createDraft(data: CreateDraftRequest): Promise<OneClickOcrDraft> {
    const response = await api.post<OneClickOcrDraft>('/api/oneclick/drafts', data);
    return response.data;
  },

  async updateDraft(id: number, data: UpdateDraftRequest): Promise<OneClickOcrDraft> {
    const response = await api.put<OneClickOcrDraft>(`/api/oneclick/drafts/${id}`, data);
    return response.data;
  },

  async downloadDraftOriginal(id: number): Promise<{ blob: Blob; filename: string }> {
    try {
      const response = await api.get<Blob>(`/api/oneclick/drafts/${id}/original`,
        { ...NO_RETRY, responseType: 'blob', timeout: 120000 });
      return { blob: response.data, filename: parseContentDispositionFilename(
        response.headers?.['content-disposition'], '시험지 원본') };
    } catch (err) {
      const message = await extractBlobErrorMessage((err as { details?: unknown })?.details);
      throw message ? { ...(err as object), message } : err;
    }
  },

  async deleteDraft(id: number): Promise<void> {
    await api.delete(`/api/oneclick/drafts/${id}`);
  },

  /**
   * Step 2: queue a background generation job for a selected OCR sample +
   * passages. Returns immediately with the job id; progress, retry and resume
   * run through the shared generation-job widget, and the worker saves the
   * result as a generation draft.
   *
   * skipRetry: every accepted request queues a new AI job (REQ-GEN-020) — an
   * automatic retry after a lost response leg could queue a duplicate.
   */
  async generate(payload: GenerateRequest): Promise<GenerateResponse> {
    const response = await api.post<GenerateResponse>('/api/oneclick/generate', payload, NO_RETRY);
    return response.data;
  },

  async getGenerationPreview(jobId: string): Promise<{
    status: string; text: string; phase: string; draft_id: number | null; error: string | null;
    queue_message?: string | null; execution_epoch?: number;
    elapsed_seconds: number | null;
  }> {
    const response = await api.get(`/api/oneclick/jobs/${encodeURIComponent(jobId)}/preview`);
    return response.data;
  },

  async getGenerationDrafts(): Promise<OneClickGenerationDraft[]> {
    const response = await api.get<{ drafts: OneClickGenerationDraft[] }>(
      '/api/oneclick/generation-drafts'
    );
    return response.data.drafts;
  },

  async createGenerationDraft(
    data: CreateGenerationDraftRequest
  ): Promise<OneClickGenerationDraft> {
    const response = await api.post<OneClickGenerationDraft>(
      '/api/oneclick/generation-drafts',
      data
    );
    return response.data;
  },

  async mergeGenerationDrafts(data: { draft_ids: number[]; title: string; category: string }): Promise<OneClickGenerationDraft> {
    const response = await api.post<OneClickGenerationDraft>(
      '/api/oneclick/generation-drafts/merge', data, NO_RETRY
    );
    return response.data;
  },

  async updateGenerationDraft(
    id: number,
    data: UpdateGenerationDraftRequest
  ): Promise<OneClickGenerationDraft> {
    const response = await api.put<OneClickGenerationDraft>(
      `/api/oneclick/generation-drafts/${id}`,
      data
    );
    return response.data;
  },

  async deleteGenerationDraft(id: number): Promise<void> {
    await api.delete(`/api/oneclick/generation-drafts/${id}`);
  },

  /**
   * Download a saved result as DOCX ('word') or HWPX with the latest saved
   * document format, using the settings page's defaults for unset preferences.
   * A failed response body arrives as a Blob, so its `error` is read here and
   * becomes the rejection's `message` (422 block reason, 500 "Export failed").
   */
  async exportOcrText(text: string, title: string, format: GenerationDraftExportFormat, options?: OneclickExportRequest): Promise<{ blob: Blob; filename: string }> {
    try {
      const saved = options ? await userPreferencesApi.getFresh() : undefined;
      const oneclickFormat = options?.resolveFormat ? options.resolveFormat(saved?.oneclickFormat) : options?.oneclickFormat;
      const response = await api.post<Blob>('/api/oneclick/ocr/export', { text, title, format,
        ...(options ? { oneclickFormat, examDate: oneclickFormat?.examDate ?? options.examDate } : {}),
      },
        { ...NO_RETRY, responseType: 'blob', timeout: 120000 });
      return {
        blob: response.data,
        filename: parseContentDispositionFilename(response.headers?.['content-disposition'],
          `시험지 읽기 결과.${format === 'word' ? 'docx' : format}`),
      };
    } catch (err) {
      const message = await extractBlobErrorMessage((err as { details?: unknown })?.details);
      throw message ? { ...(err as object), message } : err;
    }
  },

  /**
   * Download a saved result in the one-click format (SPEC-ONECLICK-FORMAT-001 design.md §3.1, §9).
   * Pending preference saves are drained first, so a change made just before the download is
   * stored before the request; a failed save blocks the request. The body carries the format the
   * download modal shows and the exam date, never the textbook keys. A failed response body
   * arrives as a Blob, so its `error` becomes the rejection's `message`.
   */
  // @MX:NOTE: [AUTO] Only caller: the one-click download modal (both entrances). The server's
  // decision order (api/oneclick.py) treats a body `oneclickFormat` as the one-click export, so
  // the textbook keys must never be added back here; the drain-then-POST order is AC-OCF-023b/c.
  // @MX:SPEC: SPEC-ONECLICK-FORMAT-001
  async exportGenerationDraft(
    id: number,
    format: GenerationDraftExportFormat,
    { oneclickFormat, examDate, resolveFormat }: OneclickExportRequest
  ): Promise<{ blob: Blob; filename: string }> {
    try {
      let savedFormat: OneclickFormat | undefined;
      try {
        const preferences = await userPreferencesApi.getFresh();
        savedFormat = preferences?.oneclickFormat;
      } catch (error) {
        const code = (error as { code?: string })?.code;
        if (resolveFormat || code === 'USER_PREFERENCES_SYNC_FAILED' || code === 'USER_SCOPE_CHANGED') {
          throw error;
        }
        // Only the re-read failed: every pending save has landed and the body carries the format.
      }
      const exportFormat = resolveFormat ? resolveFormat(savedFormat) : oneclickFormat;
      const exportDate = resolveFormat ? exportFormat.examDate ?? examDate : examDate;
      const response = await api.post<Blob>(
        `/api/oneclick/generation-drafts/${id}/export`,
        { format, oneclickFormat: exportFormat, examDate: exportDate },
        { ...NO_RETRY, responseType: 'blob', timeout: 120000 }
      );
      const filename = parseContentDispositionFilename(
        response.headers?.['content-disposition'],
        `oneclick-${id}.${format === 'word' ? 'docx' : format}`
      );
      return { blob: response.data, filename };
    } catch (err) {
      const message = await extractBlobErrorMessage((err as { details?: unknown })?.details);
      throw message ? { ...(err as object), message } : err;
    }
  },

  /**
   * Append the result to a 실전모의고사 sheet (or a new `실전모의고사 N회` sheet).
   * skipRetry: not idempotent — a retry after a lost response leg would append
   * the rows twice (REQ-OCE-015 (a)).
   */
  async sendGenerationDraftToWorkbook(
    id: number,
    payload: SendToWorkbookRequest
  ): Promise<SendToWorkbookResponse> {
    const response = await api.post<SendToWorkbookResponse>(
      `/api/oneclick/generation-drafts/${id}/send-to-workbook`,
      payload,
      NO_RETRY
    );
    return response.data;
  },

  async getPromptSetting(feature: PromptSettingFeature): Promise<PromptSetting> {
    const response = await api.get<PromptSetting>(`/api/admin/prompt-settings/${feature}`);
    return response.data;
  },

  async updatePromptSetting(
    feature: PromptSettingFeature,
    data: UpdatePromptSettingRequest
  ): Promise<PromptSetting> {
    const response = await api.put<PromptSetting>(`/api/admin/prompt-settings/${feature}`, data);
    return response.data;
  },

  async listPromptPresets(feature: PromptSettingFeature): Promise<PromptPreset[]> {
    const response = await api.get<{ presets: PromptPreset[] }>(`/api/admin/prompt-presets/${feature}`);
    return response.data.presets;
  },

  async savePromptPreset(
    feature: PromptSettingFeature,
    data: SavePromptPresetRequest
  ): Promise<PromptPreset> {
    const response = await api.post<PromptPreset>(`/api/admin/prompt-presets/${feature}`, data);
    return response.data;
  },

  async deletePromptPreset(feature: PromptSettingFeature, presetId: number): Promise<void> {
    await api.delete(`/api/admin/prompt-presets/${feature}/${presetId}`);
  },
};
